/**
 * The tenant's SSO connections — TanStack Query hooks over `/users/sso`.
 *
 * Foundations 2 (Users rail), B2d-T4. Per-domain hook file, re-exported from
 * `lib/use-users` like `use-roles`, `use-departments`, `use-user-groups` and
 * `use-invitations` (issue #134).
 *
 * THE TYPES COME FROM THE SHARED PACKAGE, not from a hand-mirrored interface
 * in this file. Zod is the type source (CLAUDE.md), and the page renders the
 * server's own read shape — which has no field a client secret could arrive in.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './api-client';
import type {
  SsoConnection,
  SsoConnectionWrite,
  SsoTestResult,
} from '@argusai/shared/schemas/sso.schema';

export type { SsoConnection, SsoConnectionWrite, SsoTestResult };

const KEY = ['users', 'sso'] as const;

export function useSsoConnections(): {
  connections: SsoConnection[];
  isLoading: boolean;
  error: unknown;
  refetch: () => void;
} {
  const { data, isLoading, error, refetch } = useQuery<{ data: SsoConnection[] }>({
    queryKey: KEY,
    queryFn: () => apiClient.get<{ data: SsoConnection[] }>('/users/sso'),
    staleTime: 15_000,
  });
  return {
    connections: data?.data ?? [],
    isLoading,
    error,
    refetch: () => {
      void refetch();
    },
  };
}

export function useCreateSsoConnection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SsoConnectionWrite) =>
      apiClient.post<SsoConnection>('/users/sso', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

export function useUpdateSsoConnection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: SsoConnectionWrite }) =>
      apiClient.put<SsoConnection>(`/users/sso/${id}`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

export function useDeleteSsoConnection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiClient.delete<{ success: true; unlinkedIdentities: number }>(
        `/users/sso/${id}`,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY });
      // A deleted connection unlinks its identities, so anything showing the
      // SSO badge is now wrong.
      qc.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

/**
 * Run the test connection.
 *
 * A FAILURE IS A 200 (decisions.md 2026-09-13 #16): the connection stays saved
 * and the failure is recorded on it, so this mutation resolves with
 * `outcome: 'failure'` rather than rejecting. The list is invalidated either
 * way, because either way the row's `lastTest` just changed.
 */
export function useTestSsoConnection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiClient.post<SsoTestResult>(`/users/sso/${id}/test`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY });
    },
  });
}
